import { useState } from "react";
import { ApplePayOptions } from "@kushki/js/lib/types/apple_pay_options";
import { defaultTo } from "lodash";
import {
  LanguageEnum,
  LanguageToAppleCode,
} from "../../../shared/infrastructure/LanguageEnum";
import { ApplePayGetTokenOptions } from "@kushki/js/lib/types/apple_pay_get_token_options";
import { AppleTokenResponse } from "@kushki/js/lib/types/apple_pay_get_token_events";
import type { ErrorResponse } from "@kushki/js/lib/types/error_response";
import {
  COUNTRY_ISO_CODE,
  CountryEnum,
} from "../../../shared/infrastructure/CountryEnum";
import { PaymentDataKindEnum } from "../../../shared/infrastructure/PaymentDataKindEnum";
import { FastPaymentsEnum } from "../../../shared/infrastructure/FastPaymentsEnum";

export interface IUseFastPaymentStateProps {
  publicMerchantId: string;
  initApplePayButton: (
    publicMerchantId: string,
    options: ApplePayOptions,
    onInit: () => void,
    onClick: () => void,
    onError: () => void
  ) => void;
  getCardApplePayToken: (
    publicMerchantId: string,
    tokenRequest: ApplePayGetTokenOptions,
    responseCallback: (response: AppleTokenResponse | ErrorResponse) => void
  ) => void;
}

export interface IUseFastPaymentState {
  handleInitFastPaymentButton: (
    paymentMethod: string,
    onCLick: () => void,
    activeLanguage?: string,
    fastPayments?: string[]
  ) => void;
  handleGetFastPaymentToken: (
    amount: number,
    currency: string,
    merchantName: string,
    country: CountryEnum,
    responseCallback: (response: AppleTokenResponse | ErrorResponse) => void
  ) => void;
  enableFastPay: boolean;
}

export const useFastPaymentState = (
  props: IUseFastPaymentStateProps
): IUseFastPaymentState => {
  const [enableFastPay, setEnableFastPay] = useState<boolean>(false);

  const isEnabledFastPay = (
    fastPayItem: string,
    fastPayments?: string[]
  ): boolean =>
    (fastPayments &&
      fastPayments.length &&
      fastPayments.includes(fastPayItem)) as boolean;

  const handleGetFastPaymentToken = (
    amount: number,
    currency: string,
    merchantName: string,
    country: CountryEnum,
    responseCallback: (response: AppleTokenResponse | ErrorResponse) => void
  ) => {
    const tokenOptions: ApplePayGetTokenOptions = {
      amount: amount,
      currencyCode: currency as ApplePayGetTokenOptions["currencyCode"],
      displayName: merchantName,
      countryCode: defaultTo(
        COUNTRY_ISO_CODE[country],
        COUNTRY_ISO_CODE[CountryEnum.ECU]
      ) as ApplePayGetTokenOptions["countryCode"],
    };

    props.getCardApplePayToken(
      props.publicMerchantId,
      tokenOptions,
      responseCallback
    );
  };

  const handleInitFastPaymentButton = (
    paymentMethod: string,
    onCLick: () => void,
    activeLanguage?: string,
    fastPayments?: string[]
  ) => {
    if (
      paymentMethod === PaymentDataKindEnum.card &&
      isEnabledFastPay(FastPaymentsEnum.APPLE_PAY, fastPayments)
    ) {
      const applePayButtonOptions: ApplePayOptions = {
        locale:
          LanguageToAppleCode[
            activeLanguage ? activeLanguage : LanguageEnum.SPANISH
          ],
        style: "black",
        type: "pay",
      };

      props.initApplePayButton(
        props.publicMerchantId,
        applePayButtonOptions,
        () => setEnableFastPay(true),
        onCLick,
        () => setEnableFastPay(false)
      );
    }
  };
  return {
    handleInitFastPaymentButton,
    handleGetFastPaymentToken,
    enableFastPay,
  };
};
